import React, { useState, useMemo } from "react";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { GoTrash } from "react-icons/go";
import { MdClose, MdRestore } from "react-icons/md";
import HeaderFilter from "../../Dashboard/DashboardTable/HeaderFilter";
import Pagination from "../../User/UserList/Pagination";
import { allCountries } from "country-telephone-data";
import profile_Img from "../../../../../Assets/Images/profile_Img.png";
import DeleteVec from "../../../../../Assets/Images/deleteVec.png";
import RestoreModal from "../Modals/RestoreModal";
import HardDeleteModal from "../Modals/HardDeleteModal";

export default function DeletedUsersList({
    setSelectedUser,
    userList,
    isLoading,
    getDeletedUserList,
    currentPage,
    totalPages,
    totalUsers,
    restoreUser,
    hardDeleteUser,
    selectedTime,
    setSelectedTime,
}) {
    const [sortConfig, setSortConfig] = useState({
        key: "deletedAt",
        direction: "desc",
    });
    const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
    const [isHardDeleteModalOpen, setIsHardDeleteModalOpen] = useState(false);
    const [selectedUserForAction, setSelectedUserForAction] = useState(null);
    const [isRestoring, setIsRestoring] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const sortedData = useMemo(() => {
        return [...userList].sort((a, b) => {
            if (sortConfig.key === "deletedAt" || sortConfig.key === "createdAt") {
                return sortConfig.direction === "asc"
                    ? new Date(a[sortConfig.key]) - new Date(b[sortConfig.key])
                    : new Date(b[sortConfig.key]) - new Date(a[sortConfig.key]);
            }
            if (sortConfig.key === "countryCode") {
                return sortConfig.direction === "asc"
                    ? a.countryCode.localeCompare(b.countryCode)
                    : b.countryCode.localeCompare(a.countryCode);
            }
            if (a[sortConfig.key]?.toLowerCase() < b[sortConfig.key]?.toLowerCase())
                return sortConfig.direction === "asc" ? -1 : 1;
            if (a[sortConfig.key]?.toLowerCase() > b[sortConfig.key]?.toLowerCase())
                return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
    }, [sortConfig, userList]);

    const handleSort = (key) => {
        let direction = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });
    };

    const getCountryFromPhoneCode = (code) => {
        const phoneCode = code?.replace("+", "");
        const match = allCountries.find((c) => c.dialCode === phoneCode);
        return match ? match.name : "Unknown";
    };

    const formatDate = (isoString) => {
        const date = new Date(isoString);
        return date.toLocaleString("en-US", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const openRestoreModal = (user) => {
        setSelectedUserForAction(user);
        setSelectedUser(user);
        setIsRestoreModalOpen(true);
    };

    const openHardDeleteModal = (user) => {
        setSelectedUserForAction(user);
        setSelectedUser(user);
        setIsHardDeleteModalOpen(true);
    };

    const closeRestoreModal = () => {
        setIsRestoreModalOpen(false);
        setSelectedUserForAction(null);
    };

    const closeHardDeleteModal = () => {
        setIsHardDeleteModalOpen(false);
        setSelectedUserForAction(null);
    };

    return (
        <section>
            <HeaderFilter
                getUserList={getDeletedUserList}
                selectedTime={selectedTime}
                setSelectedTime={setSelectedTime}
            />

            <div className={`overflow-x-auto ${isLoading ? 'h-screen overflow-hidden' : ''}`}>
                <table className="min-w-full whitespace-nowrap border-0 rounded-md">
                    <thead className="bg-[#F9F7FF] text-sm font-medium text-gray-700">
                        <tr>
                            {[
                                { key: "name", label: "User Name" },
                                { key: "email", label: "Email Address" },
                                { key: "phone", label: "Phone Number" },
                                { key: "createdAt", label: "Reg Date" },
                                { key: "deletedAt", label: "Deleted Date" },
                                { key: "countryCode", label: "Country" },
                            ].map(({ key, label }) => (
                                <th
                                    key={key}
                                    className="px-4 py-4 font-medium cursor-pointer select-none"
                                    onClick={() => handleSort(key)}
                                >
                                    <div className="flex items-center gap-2">
                                        {label}
                                        {sortConfig.key === key ? (
                                            sortConfig.direction === "asc" ? (
                                                <FaSortUp />
                                            ) : (
                                                <FaSortDown />
                                            )
                                        ) : (
                                            <FaSort className="text-gray-300" />
                                        )}
                                    </div>
                                </th>
                            ))}
                            <th className="px-4 py-4 font-medium">Action</th>
                        </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-100 text-sm">
                        {isLoading ? (
                            <tr>
                                <td colSpan="7" className="text-center p-5 bg-white">
                                    Loading...
                                </td>
                            </tr>
                        ) : sortedData.length > 0 ? (
                            sortedData.map((user, index) => (
                                <tr
                                    key={index}
                                    className={`bg-white hover:bg-gray-50 ${index === 0 ? "border-t-0" : ""
                                        }`}
                                >
                                    <td className="px-4 py-4">
                                        <div className="flex items-center gap-3">
                                            <img
                                                src={user.userImage || user.avtarImage || profile_Img}
                                                alt={user.name}
                                                className="w-8 h-8 rounded-full object-cover"
                                            />
                                            <span>{user.name}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4">{user.email || "-"}</td>
                                    <td className="px-4 py-4">{user.phone || "-"}</td>
                                    <td className="px-4 py-4">{user.createdAt ? formatDate(user.createdAt) : "-"}</td>
                                    <td className="px-4 py-4 text-red-600 font-medium">
                                        {user.deletedAt ? formatDate(user.deletedAt) : "-"}
                                    </td>
                                    <td className="px-4 py-4">
                                        {user.countryName || (user.countryCode ? getCountryFromPhoneCode(user.countryCode) : "-")}
                                    </td>
                                    <td className="px-4 py-4 flex items-center gap-3 text-gray-600">
                                        <button
                                            title="Restore User"
                                            className="bg-[#E8F5E9] rounded-md h-[30px] w-[30px] flex items-center justify-center text-[#4CAF50] hover:opacity-50"
                                            onClick={() => openRestoreModal(user)}
                                        >
                                            <MdRestore />
                                        </button>
                                        <button
                                            title="Hard Delete"
                                            className="bg-[#FFEFEE] rounded-md h-[30px] w-[30px] flex items-center justify-center text-[#F44336] hover:opacity-50"
                                            onClick={() => openHardDeleteModal(user)}
                                        >
                                            <GoTrash />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={8} className="text-center bg-white py-2">
                                    No deleted users found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {!isLoading && totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalItems={totalUsers}
                    itemsPerPage={10}
                    onPageChange={(page) => {
                        getDeletedUserList(page);
                    }}
                />
            )}

            <RestoreModal
                isOpen={isRestoreModalOpen}
                onClose={closeRestoreModal}
                onConfirm={async () => {
                    setIsRestoring(true);
                    const success = await restoreUser();
                    if (success) {
                        closeRestoreModal();
                    }
                    setIsRestoring(false);
                }}
                user={selectedUserForAction}
                isRestoring={isRestoring}
            />

            <HardDeleteModal
                isOpen={isHardDeleteModalOpen}
                onClose={closeHardDeleteModal}
                onConfirm={async () => {
                    setIsDeleting(true);
                    const success = await hardDeleteUser();
                    if (success) {
                        closeHardDeleteModal();
                    }
                    setIsDeleting(false);
                }}
                user={selectedUserForAction}
                isDeleting={isDeleting}
            />
        </section>
    );
}
